import { Navigate, useLocation } from 'react-router';

import { AuthStateType, useAuth } from '../../hooks/useAuth';
import {
  getFullLocationPath,
  getSafeFromLocation,
} from '../../utils/navigation';
import {
  hasAdminAccess,
  hasDappStaffAccess,
  hasHubspotTicketAccess,
  hasPublisherStaffAccess,
  hasSuperadminAccess,
} from '../../utils/roleUtils';

interface Props {
  children?: React.ReactNode;
}

function canAccessAdminRoute(
  pathname: string,
  role: string,
  email: string | null | undefined,
) {
  return (
    (pathname.startsWith('/admin') ||
      pathname.startsWith('/app-spotlight') ||
      isQuestsRoute(pathname) ||
      pathname.startsWith('/reporting')) &&
    hasAdminAccess(role as Parameters<typeof hasAdminAccess>[0], email)
  );
}

function canAccessPublisherStaffRoute(
  pathname: string,
  role: string,
  email: string | null | undefined,
) {
  return (
    pathname.startsWith('/publishers') &&
    hasPublisherStaffAccess(
      role as Parameters<typeof hasPublisherStaffAccess>[0],
      email,
    )
  );
}

function canAccessDappStaffRoute(
  pathname: string,
  role: string,
  email: string | null | undefined,
) {
  return (
    (pathname.startsWith('/dapps') || pathname.startsWith('/dapp/')) &&
    hasDappStaffAccess(role as Parameters<typeof hasDappStaffAccess>[0], email)
  );
}

function canAccessHubspotTicketRoute(
  pathname: string,
  role: string,
  email: string | null | undefined,
) {
  return (
    pathname.startsWith('/tickets') &&
    hasHubspotTicketAccess(
      role as Parameters<typeof hasHubspotTicketAccess>[0],
      email,
    )
  );
}

function isPublisherAccessRoute(pathname: string) {
  return pathname === '/publisher-access';
}

function isAppSpotlightRoute(pathname: string) {
  return (
    pathname === '/app-spotlight' || pathname.startsWith('/app-spotlight/')
  );
}

function isQuestsRoute(pathname: string) {
  return pathname === '/quests' || pathname.startsWith('/quests/');
}

function canAccessWhileEmailUnverified(
  pathname: string,
  role: string,
  email: string | null | undefined,
) {
  return (
    (pathname.startsWith('/super') &&
      hasSuperadminAccess(
        role as Parameters<typeof hasSuperadminAccess>[0],
        email,
      )) ||
    canAccessAdminRoute(pathname, role, email) ||
    canAccessPublisherStaffRoute(pathname, role, email) ||
    canAccessDappStaffRoute(pathname, role, email) ||
    canAccessHubspotTicketRoute(pathname, role, email)
  );
}

export function RequiresAuthentication(props: Props) {
  const [auth] = useAuth();
  const location = useLocation();
  const currentLocation = getFullLocationPath(location);
  const from = getSafeFromLocation(location.state?.from) ?? currentLocation;
  const completedPublisherAccessRedirect =
    from !== currentLocation ? from : '/dashboard';

  switch (auth.type) {
    case AuthStateType.Authenticated: {
      if (
        isAppSpotlightRoute(location.pathname) &&
        !hasAdminAccess(auth.user.role, auth.user.email)
      ) {
        return <Navigate to="/dashboard" replace />;
      }

      if (
        isQuestsRoute(location.pathname) &&
        !hasAdminAccess(auth.user.role, auth.user.email)
      ) {
        return <Navigate to="/dashboard" replace />;
      }

      if (
        !auth.user.emailVerified &&
        auth.user.onboardingState !== 'EmailVerification'
      ) {
        if (
          canAccessWhileEmailUnverified(
            location.pathname,
            auth.user.role,
            auth.user.email,
          )
        ) {
          return props.children;
        }

        if (location.pathname === '/verify-email') {
          return props.children;
        }

        return <Navigate to="/verify-email" state={{ from }} />;
      }

      switch (auth.user.onboardingState) {
        case 'EmailVerification': {
          if (
            canAccessWhileEmailUnverified(
              location.pathname,
              auth.user.role,
              auth.user.email,
            )
          ) {
            return props.children;
          }

          if (location.pathname === '/verify-email') {
            return props.children;
          }

          return <Navigate to="/verify-email" state={{ from }} />;
        }
        case 'KYB': {
          if (isPublisherAccessRoute(location.pathname)) {
            return <Navigate to={completedPublisherAccessRedirect} replace />;
          }

          return props.children;
        }
        case 'ProfileCreation': {
          // Allow superadmins to access /super and sub-routes regardless of onboarding state
          if (
            location.pathname.startsWith('/super') &&
            hasSuperadminAccess(auth.user.role, auth.user.email)
          ) {
            return props.children;
          }

          // Allow admins to access /admin and sub-routes regardless of onboarding state
          if (
            canAccessAdminRoute(
              location.pathname,
              auth.user.role,
              auth.user.email,
            ) ||
            canAccessPublisherStaffRoute(
              location.pathname,
              auth.user.role,
              auth.user.email,
            ) ||
            canAccessDappStaffRoute(
              location.pathname,
              auth.user.role,
              auth.user.email,
            ) ||
            canAccessHubspotTicketRoute(
              location.pathname,
              auth.user.role,
              auth.user.email,
            )
          ) {
            return props.children;
          }

          if (
            location.pathname === '/publisher-access' ||
            location.pathname === '/create-profile'
          ) {
            return props.children;
          }

          return <Navigate to="/publisher-access" state={{ from }} />;
        }
        case 'Complete': {
          if (isPublisherAccessRoute(location.pathname)) {
            return <Navigate to={completedPublisherAccessRedirect} replace />;
          }

          return props.children;
        }
        default: {
          // This should never happen, but handle it gracefully
          // by treating unknown states as complete
          return props.children;
        }
      }
    }
    case AuthStateType.NoToken:
      return <Navigate to="/auth" state={{ from }} />;
    case AuthStateType.Verifying:
      return null;
  }
}
